/** Epic 93 — customer-facing safety-manager labels (display only; backend fields unchanged). */

export type SafetyManagerUxLabelKey =
  | 'pattern_owner'
  | 'followup_owner'
  | 'followup_measurement'
  | 'control_status'
  | 'open_linked_records'
  | 'pattern_volume'
  | 'evidence_quality'
  | 'measurement_basis'
  | 'hazard_class'
  | 'risk_category'
  | 'incident_group'
  | 'corrective_action'
  | 'escalation'
  | 'safety_case'
  | 'evidence_package'
  | 'claim_gates'
  | 'technical_details'
  | 'report_mode_pattern'
  | 'report_mode_hazard_class'
  | 'impact_tracking';

type SafetyManagerUxLabelEntry = {
  label: string;
  helper?: string;
  legacyLabels: string[];
};

export const SAFETY_MANAGER_UX_LABELS: Record<SafetyManagerUxLabelKey, SafetyManagerUxLabelEntry> = {
  pattern_owner: {
    label: 'Risk owner',
    helper: 'Person responsible for understanding and managing the recurring risk pattern.',
    legacyLabels: ['Pattern owner', 'Rule owner'],
  },
  followup_owner: {
    label: 'Action owner',
    helper: 'Person responsible for follow-up or corrective action.',
    legacyLabels: ['Follow-up owner'],
  },
  followup_measurement: {
    label: 'Impact measurement',
    helper:
      'Whether the issue improved after action was taken. Not measured means impact measurement has not been checked yet.',
    legacyLabels: ['Follow-up measurement', 'Outcome measurement'],
  },
  control_status: {
    label: 'Risk status',
    helper: 'Whether this issue is new, under review, assigned for action, or being tracked for improvement.',
    legacyLabels: ['Control status'],
  },
  open_linked_records: {
    label: 'Related incidents / events',
    helper: 'Individual camera-detected events that make up this recurring risk.',
    legacyLabels: ['Open linked records'],
  },
  pattern_volume: {
    label: 'Events in last 7 days',
    helper:
      'How many related events were linked to this recurring risk in the selected lookback window (168h = 7 days).',
    legacyLabels: ['Pattern volume', 'Risk frequency'],
  },
  evidence_quality: {
    label: 'Evidence strength',
    helper:
      'How reliable the available camera observations and evidence items are. Not evaluated means no human review has confirmed evidence strength yet.',
    legacyLabels: ['Evidence quality', 'Evidence/proof'],
  },
  measurement_basis: {
    label: 'Detection basis',
    helper:
      'What the system used to detect the issue: zone entry, camera-frame proximity, line crossing, or calibrated floor-plane distance.',
    legacyLabels: ['Measurement basis', 'Measurement'],
  },
  hazard_class: {
    label: 'Risk category',
    helper: 'The precursor hazard family used to group recurring safety risks.',
    legacyLabels: ['Hazard class'],
  },
  risk_category: {
    label: 'Risk category',
    helper: 'The precursor hazard family used to group recurring safety risks.',
    legacyLabels: ['Hazard class'],
  },
  incident_group: {
    label: 'Related event group',
    legacyLabels: ['Incident group'],
  },
  corrective_action: {
    label: 'Safety action',
    legacyLabels: ['Corrective action'],
  },
  escalation: {
    label: 'Needs attention',
    legacyLabels: ['Escalation'],
  },
  safety_case: {
    label: 'Review record',
    legacyLabels: ['SafetyCase', 'Safety case', 'Representative safety case'],
  },
  evidence_package: {
    label: 'Evidence details',
    legacyLabels: ['Evidence Package', 'Evidence package'],
  },
  claim_gates: {
    label: 'Claim limits',
    legacyLabels: ['Claim gates'],
  },
  technical_details: {
    label: 'Advanced technical details',
    legacyLabels: ['Technical Details', 'Technical details'],
  },
  report_mode_pattern: {
    label: 'Recurring risk pattern',
    legacyLabels: ['pattern mode', 'Recurring safety pattern'],
  },
  report_mode_hazard_class: {
    label: 'Risk category report',
    legacyLabels: ['Hazard-class report', 'hazard_class mode'],
  },
  impact_tracking: {
    label: 'Impact measurement',
    helper: 'Whether the issue improved after action was taken.',
    legacyLabels: ['Follow-up measurement', 'Outcome measurement'],
  },
};

const LEGACY_LABEL_LOOKUP = new Map<string, SafetyManagerUxLabelKey>(
  Object.entries(SAFETY_MANAGER_UX_LABELS).flatMap(([key, entry]) =>
    entry.legacyLabels.map((legacy) => [legacy.toLowerCase(), key as SafetyManagerUxLabelKey]),
  ),
);

export function displaySafetyManagerLabel(key: SafetyManagerUxLabelKey): string {
  return SAFETY_MANAGER_UX_LABELS[key].label;
}

export function displaySafetyManagerHelper(key: SafetyManagerUxLabelKey): string | undefined {
  return SAFETY_MANAGER_UX_LABELS[key].helper;
}

export function resolveSafetyManagerHelperForDisplayLabel(label: string): string | undefined {
  const normalized = String(label || '').trim().toLowerCase();
  if (!normalized) return undefined;
  for (const entry of Object.values(SAFETY_MANAGER_UX_LABELS)) {
    if (entry.label.toLowerCase() === normalized) return entry.helper;
  }
  return undefined;
}

function finalizeLoosePatternWordRemap(text: string): string {
  return String(text || '')
    .replace(/\bpatterns\b/gi, 'risks')
    .replace(/\bpattern\b/gi, 'risk');
}

/**
 * Investigate canonical vocabulary. Epic 93 still remaps other "pattern" copy to "risk",
 * but these exact Investigate labels must stay Pattern/Patterns (not Risk/Risks).
 */
const INVESTIGATE_CANONICAL_LABELS = new Set(
  [
    'PATTERNS',
    'Patterns',
    'OBSERVATIONS',
    'INDIVIDUAL OBSERVATIONS',
    'Observations',
    'Individual observations',
    'Pattern status',
    'Pattern Review',
    'Investigate',
    'Needs attention',
    'High severity',
    'Recurring',
    'Stale reviews',
    'Review queue',
    'Patterns needing attention',
    'Patterns needing review',
    'Stale pattern reviews',
    '1 pattern needs review',
    'Open observations',
    'Review observation',
    'Back to observations',
    'Back to patterns',
    'Back to pattern',
    'Pattern decision',
    'Drill into observations',
  ].map((label) => label.toLowerCase()),
);

/**
 * Safety Scan canonical vocabulary. Epic 93 still remaps other copy, but these Scan
 * labels must not become Review record / Risk / claim-signing language.
 */
const SAFETY_SCAN_CANONICAL_LABELS = new Set(
  [
    'Safety Scan',
    'Draft Safety Scan',
    'Signed Safety Scan',
    'Scan Scope',
    'Scan Package',
    'Safety Case',
    'Safety Cases',
    'Linked Safety Case',
    'Linked Safety Cases',
    'Measured signals',
    'Confirm true event',
    'False positive',
    'Duplicate',
    'Needs more review',
    'Rate withheld · insufficient usable coverage',
  ].map((label) => label.toLowerCase()),
);

/**
 * Safety Cases Workspace canonical vocabulary. Smallest Epic 93 exception:
 * do not turn Safety Case into Review Record on this management surface.
 */
const SAFETY_CASES_WORKSPACE_CANONICAL_LABELS = new Set(
  [
    'Safety Case',
    'Safety Cases',
    'Open Safety Case',
    'View Safety Scan',
    'Sign Safety Scan',
    'Latest Safety Scan',
    'Safety Scans',
    'Linked Safety Case',
    'Patterns needing review',
    'Active Safety Cases',
    'View Record',
  ].map((label) => label.toLowerCase()),
);

export function remapLegacySafetyManagerLabel(label: string): string {
  const raw = String(label || '').trim();
  if (!raw) return raw;
  if (/^observations$/i.test(raw)) {
    return raw === 'OBSERVATIONS' ? 'INDIVIDUAL OBSERVATIONS' : 'Individual observations';
  }
  if (INVESTIGATE_CANONICAL_LABELS.has(raw.toLowerCase())) return raw;
  if (SAFETY_SCAN_CANONICAL_LABELS.has(raw.toLowerCase())) return raw;
  if (SAFETY_CASES_WORKSPACE_CANONICAL_LABELS.has(raw.toLowerCase())) return raw;
  if (/\bsafety scan\b/i.test(raw) && /\bsafety case\b/i.test(raw)) return raw;
  if (/^open safety case$/i.test(raw) || /^view safety scan$/i.test(raw)) return raw;
  if (/^(pattern volume|risk frequency),\s*168h$/i.test(raw)) {
    return displaySafetyManagerLabel('pattern_volume');
  }
  const key = LEGACY_LABEL_LOOKUP.get(raw.toLowerCase());
  if (key) return displaySafetyManagerLabel(key);
  const normalized = raw.toLowerCase();
  const customerCopyReplacements: Array<[string, string]> = [
    ['pattern review', 'Risk review'],
    ['pattern decision', 'Risk decision'],
    ['recurring safety pattern', 'Recurring risk pattern'],
    ['follow-up owner', 'Action owner'],
    ['follow-up ownership', 'Action ownership'],
    ['follow-up measurement', 'Impact measurement'],
    ['outcome measurement', 'Impact measurement'],
    ['technical details', 'Advanced technical details'],
    ['follow-up scheduled', 'Action scheduled'],
    ['ready for follow-up', 'Ready for impact measurement'],
    ['overdue follow-up', 'Overdue action'],
    ['confirm pattern owner', 'Confirm risk owner'],
    ['assign pattern owner', 'Assign risk owner'],
    ['pattern owner', 'Risk owner'],
    ['pattern volume', 'Risk frequency'],
    ['pattern context', 'Risk context'],
    ['pattern state', 'Risk state'],
    ['main pattern', 'Main recurring risk'],
    ['pattern source', 'Risk source'],
    ['pattern group', 'Risk group'],
    ['key incident pattern', 'Key recurring risk'],
    ['linked critical-risk pattern', 'Safety condition'],
    ['linked critical-risk patterns', 'Safety conditions'],
    ['critical-risk patterns', 'recurring risks'],
    ['critical-risk pattern', 'recurring risk'],
    ['exposure patterns', 'recurring risks'],
    ['repeated pattern', 'recurring risk'],
    ['repeat pattern', 'recurring risk'],
    ['incident pattern', 'recurring risk'],
    ['safety pattern', 'safety risk'],
    ['main repeated pattern', 'main recurring risk'],
    ['durable repeat pattern', 'durable recurring risk'],
    ['recurrence pattern', 'recurring risk'],
    ['grouped-incident pattern', 'grouped-incident risk signal'],
    ['pattern action', 'group action'],
    ['pattern label', 'risk label'],
    ['on-floor patterns', 'on-floor risks'],
    ['exposure pattern', 'exposure risk'],
    ['strongest pattern', 'strongest recurring risk'],
    ['this incident pattern', 'this recurring risk'],
    ['emerging pattern', 'emerging recurring risk'],
    ['non-compliance patterns', 'non-compliance risks'],
    ['risk patterns are', 'recurring risks are'],
    ['risk pattern', 'recurring risk'],
    ['read-side pattern volume', 'read-side risk frequency'],
    ['matching incident pattern', 'matching recurring risk'],
  ];
  let remapped = raw;
  for (const [from, to] of customerCopyReplacements) {
    if (normalized.includes(from)) {
      remapped = remapped.replace(new RegExp(from, 'gi'), to);
    }
  }
  if (remapped !== raw) return finalizeLoosePatternWordRemap(remapped);
  if (/^pattern volume\b/i.test(raw)) {
    return raw.replace(/^pattern volume/i, displaySafetyManagerLabel('pattern_volume'));
  }
  if (/^evidence quality:/i.test(raw)) {
    return raw.replace(/^evidence quality/i, displaySafetyManagerLabel('evidence_quality'));
  }
  if (/^follow-up owner:/i.test(raw)) {
    return raw.replace(/^follow-up owner/i, displaySafetyManagerLabel('followup_owner'));
  }
  if (/^follow-up owner unassigned$/i.test(raw)) {
    return `${displaySafetyManagerLabel('followup_owner')} unassigned`;
  }
  if (/^pattern owner:/i.test(raw)) {
    return raw.replace(/^pattern owner/i, displaySafetyManagerLabel('pattern_owner'));
  }
  if (/\bpattern(s)?\b/i.test(raw)) {
    return finalizeLoosePatternWordRemap(raw);
  }
  return raw;
}

/** Apply Epic 93 customer-facing terminology to free-form UI copy. */
export function remapLegacyCustomerFacingCopy(text: string): string {
  return remapLegacySafetyManagerLabel(text);
}

export function mapSafetyManagerFactLabels<T extends { label: string; value?: string }>(
  facts: T[],
): T[] {
  return facts.map((fact) => ({
    ...fact,
    label: remapLegacySafetyManagerLabel(fact.label),
  }));
}

export function riskFrequencyLabel(lookbackLabel: string): string {
  const window = String(lookbackLabel || '168h').trim() || '168h';
  if (window === '168h' || window === '168') {
    return displaySafetyManagerLabel('pattern_volume');
  }
  const hours = Number.parseInt(window.replace(/h$/i, ''), 10);
  if (hours === 168) return displaySafetyManagerLabel('pattern_volume');
  if (Number.isFinite(hours) && hours > 0 && hours % 24 === 0) {
    const days = hours / 24;
    return `Events in last ${days} day${days === 1 ? '' : 's'}`;
  }
  return `Risk frequency, last ${window}`;
}

export function formatActionOwnerChipLabel(args: {
  owner: string;
  currentUser?: string | null;
}): string {
  const owner = String(args.owner || '').trim() || 'Unassigned';
  const label = displaySafetyManagerLabel('followup_owner');
  if (owner === 'Unassigned') return `${label} unassigned`;
  const user = String(args.currentUser || '').trim();
  if (user && owner.toLowerCase() === user.toLowerCase()) return `${label}: me`;
  return `${label}: ${owner}`;
}

export function formatRiskOwnerChipLabel(owner: string): string {
  const value = String(owner || '').trim() || 'Unassigned';
  const label = displaySafetyManagerLabel('pattern_owner');
  return value === 'Unassigned' ? `${label}: Unassigned` : `${label}: ${value}`;
}
